import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import ky from "ky";
import { defaultHeaders } from "../../guides/types";
import { type OutageApiItem, useOutageInfoStore } from "../store";

interface OutageResponse {
    outages: OutageApiItem[];
    allClear: boolean;
    timestamp: string;
}

export const useFetchOutageInfo = (logger: Logger) => {
    const { dispatcher } = useReactAnalytics();

    const fetchOutageInfo = async (ssoAccessToken: string) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/api/outages/v1/active`;
            const response = await ky.get(url, {
                headers: {
                    ...defaultHeaders,
                    Authorization: `Bearer ${ssoAccessToken}`,
                },
                timeout: 30000,
            });
            const data = await response.json<OutageResponse>();

            logger.info(data, "Outages | Service returned active outages");

            const { setOutages } = useOutageInfoStore.getState();

            setOutages({ outages: data.outages, allClear: data.allClear, timestamp: data.timestamp });

            if (data.outages.length > 0) {
                const analyticsPayload = {
                    outagesCount: data.outages.length,
                    outages: data.outages.map((o) => o.slug),
                    allClear: data.allClear,
                };

                void dispatcher.dispatchBusinessEvent("Outage_InfoReceived", analyticsPayload);

                trackFullStoryEvent("Outage_InfoReceived", analyticsPayload);
            }

            return data;
        } catch (error) {
            logger.error({ error }, "Outages | Error fetching active outages");

            const errorPayload = {
                error: error instanceof Error ? error.message : String(error),
            };

            void dispatcher.dispatchBusinessEvent("Outage_FetchError", errorPayload);

            trackFullStoryEvent("Outage_FetchError", errorPayload);

            return { outages: [], allClear: true, timestamp: new Date().toISOString() };
        }
    };

    return { fetchOutageInfo };
};
